/**
 * JWT claim decoding (NO signature verification — server is the source of
 * truth). We decode the payload locally only to populate UI state and to
 * check expiry without a round trip.
 */

import type { JWTClaims } from "@/types/api";

export function decodeJWT(token: string | null | undefined): JWTClaims | null {
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const payload = parts[1];
    const json = JSON.parse(b64UrlDecode(payload));
    return json as JWTClaims;
  } catch {
    return null;
  }
}

export function isExpired(token: string | null | undefined): boolean {
  const claims = decodeJWT(token);
  if (!claims || typeof claims.exp !== "number") return true;
  // Add 5s skew so a token that just expired isn't treated as still valid
  return claims.exp * 1000 < Date.now() + 5000;
}

function b64UrlDecode(s: string): string {
  const pad = "=".repeat((4 - (s.length % 4)) % 4);
  const b64 = (s + pad).replace(/-/g, "+").replace(/_/g, "/");
  // atob is browser-side; in tests we run jsdom which provides it
  return decodeURIComponent(
    atob(b64)
      .split("")
      .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
      .join("")
  );
}
